'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["ownerState", "slots", "slotProps"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useChatLocaleText } from "../chat/internals/ChatLocaleContext.js";
import { useIsHydrated } from "../chat/internals/useIsHydrated.js";
import { ProgressIndicator, ProgressRoot, ProgressTrack } from "../internals/ProgressSlots.js";
import { useMessageContext } from "./internals/MessageContext.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const MessageMeta = /*#__PURE__*/React.forwardRef(function MessageMeta(props, ref) {
  const _ref = props,
    {
      ownerState: ownerStateProp,
      slots,
      slotProps
    } = _ref,
    other = _objectWithoutPropertiesLoose(_ref, _excluded);
  const ownerState = useMessageContext();
  const localeText = useChatLocaleText();
  const isHydrated = useIsHydrated();
  const timestampLabel = isHydrated && ownerState.message?.createdAt ? localeText.messageTimestampLabel(ownerState.message.createdAt) : '';
  // Show the timestamp in the per-message meta row for all variants,
  // so it appears inline with the status indicator (e.g. "Sent · 10:55").
  const showTimestamp = Boolean(timestampLabel);
  // Delivery statuses ("Sent", "Read") are only meaningful for outgoing (user) messages.
  // For other roles, skip them — showing "Sent" under an incoming message is confusing.
  const messageStatus = ownerState.message?.status;
  const isDeliveryStatus = messageStatus === 'sent' || messageStatus === 'read';
  const showStatus = messageStatus && !(isDeliveryStatus && ownerState.message?.role !== 'user');
  const statusLabel = showStatus ? localeText.messageStatusLabel(messageStatus) : '';
  const hasMeta = showTimestamp || Boolean(statusLabel) || ownerState.message?.editedAt != null;
  void ownerStateProp;
  const Meta = slots?.meta ?? 'div';
  const Timestamp = slots?.timestamp ?? 'span';
  const Status = slots?.status ?? 'span';
  const Edited = slots?.edited ?? 'span';
  const StreamingProgress = slots?.streamingProgress ?? ProgressRoot;
  const StreamingProgressTrack = slots?.streamingProgressTrack ?? ProgressTrack;
  const StreamingProgressIndicator = slots?.streamingProgressIndicator ?? ProgressIndicator;
  const metaProps = useSlotProps({
    elementType: Meta,
    externalSlotProps: slotProps?.meta,
    externalForwardedProps: other,
    ownerState,
    additionalProps: {
      ref
    }
  });
  const timestampProps = useSlotProps({
    elementType: Timestamp,
    externalSlotProps: slotProps?.timestamp,
    ownerState
  });
  const statusProps = useSlotProps({
    elementType: Status,
    externalSlotProps: slotProps?.status,
    ownerState
  });
  const editedProps = useSlotProps({
    elementType: Edited,
    externalSlotProps: slotProps?.edited,
    ownerState
  });
  const streamingProgressProps = useSlotProps({
    elementType: StreamingProgress,
    externalSlotProps: slotProps?.streamingProgress,
    ownerState,
    additionalProps: {
      // value={null} → indeterminate state → aria-valuetext="loading" by default
      value: null,
      'aria-label': localeText.messageStatusLabel('streaming')
    }
  });
  const streamingProgressTrackProps = useSlotProps({
    elementType: StreamingProgressTrack,
    externalSlotProps: slotProps?.streamingProgressTrack,
    ownerState
  });
  const streamingProgressIndicatorProps = useSlotProps({
    elementType: StreamingProgressIndicator,
    externalSlotProps: slotProps?.streamingProgressIndicator,
    ownerState
  });
  if (!hasMeta && !ownerState.streaming) {
    return null;
  }
  return /*#__PURE__*/_jsxs(Meta, _extends({}, metaProps, {
    children: [ownerState.streaming ? /*#__PURE__*/_jsx(StreamingProgress, _extends({}, streamingProgressProps, {
      children: /*#__PURE__*/_jsx(StreamingProgressTrack, _extends({}, streamingProgressTrackProps, {
        children: /*#__PURE__*/_jsx(StreamingProgressIndicator, _extends({}, streamingProgressIndicatorProps))
      }))
    })) : null, ownerState.message?.status && statusLabel && !ownerState.streaming ? /*#__PURE__*/_jsx(Status, _extends({}, statusProps, {
      children: statusLabel
    })) : null, ownerState.message?.editedAt ? /*#__PURE__*/_jsx(Edited, _extends({}, editedProps, {
      children: localeText.messageEditedLabel
    })) : null, showTimestamp ? /*#__PURE__*/_jsx(Timestamp, _extends({}, timestampProps, {
      children: timestampLabel
    })) : null]
  }));
});
if (process.env.NODE_ENV !== "production") MessageMeta.displayName = "MessageMeta";